/** BoosterStore — ステージ開始前ブースター 3 種の残数を localStorage で永続化。
 *  Reference: docs/CLAUDE.md §3.3 Skill Honored
 *  - brushBoost(疾走筆 1.5x 3 秒)/ bigStart(大胆な始まり +2 領地)/ extraLife(余分な命 +1)
 *  - 課金で直接買えない(Skill Honored 死守):広告・ステージクリア・ショップ Color 経由のみ
 *
 *  Callers:
 *    - web/src/main.ts: 生成 + grantStageClearRandom
 *    - web/src/ui/screens/HomeScreen.ts: booster-pouch でカウント表示 + consume
 *    - web/src/ui/screens/ShopScreen.ts: ブースター個別販売(Color 消費)
 */

export type BoosterId = 'brushBoost' | 'bigStart' | 'extraLife';
export const BOOSTER_IDS: readonly BoosterId[] = ['brushBoost', 'bigStart', 'extraLife'] as const;

export interface BoosterSnapshot {
  brushBoost: number;
  bigStart: number;
  extraLife: number;
}

export const BOOSTER_META: Record<BoosterId, { name: string; icon: string; descJa: string }> = {
  brushBoost: { name: 'Brush Boost', icon: '⚡', descJa: '開始 3 秒間 1.5x 速度' },
  bigStart:   { name: 'Big Start',   icon: '🟧', descJa: '初期領地 +2 セル拡張' },
  extraLife:  { name: 'Extra Life',  icon: '❤️', descJa: '失敗回数 +1' },
};

const STORAGE_KEY = 'ck.boosters';
const DEFAULT_INVENTORY: BoosterSnapshot = { brushBoost: 1, bigStart: 1, extraLife: 1 };

export class BoosterStore {
  private state: BoosterSnapshot;

  constructor() {
    this.state = this.load();
  }

  private load(): BoosterSnapshot {
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return { ...DEFAULT_INVENTORY };
      const parsed = JSON.parse(raw) as Partial<BoosterSnapshot>;
      return {
        brushBoost: typeof parsed.brushBoost === 'number' && parsed.brushBoost >= 0 ? Math.floor(parsed.brushBoost) : 0,
        bigStart:   typeof parsed.bigStart   === 'number' && parsed.bigStart   >= 0 ? Math.floor(parsed.bigStart)   : 0,
        extraLife:  typeof parsed.extraLife  === 'number' && parsed.extraLife  >= 0 ? Math.floor(parsed.extraLife)  : 0,
      };
    } catch {
      return { ...DEFAULT_INVENTORY };
    }
  }

  private save(): void {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      /* noop(プライベートモード等) */
    }
  }

  snapshot(): BoosterSnapshot {
    return { ...this.state };
  }

  count(id: BoosterId): number {
    return this.state[id];
  }

  add(id: BoosterId, n = 1): void {
    this.state[id] = Math.max(0, this.state[id] + n);
    this.save();
  }

  consume(id: BoosterId): boolean {
    if (this.state[id] <= 0) return false;
    this.state[id] -= 1;
    this.save();
    return true;
  }

  /** ステージクリア時にランダム 1 種に +1 付与(Skill Honored 補充) */
  grantStageClearRandom(): BoosterId {
    const id = BOOSTER_IDS[Math.floor(Math.random() * BOOSTER_IDS.length)]!;
    this.add(id, 1);
    return id;
  }
}
